import React from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  TextField,
  InputLabel,
} from '@mui/material';
import { useSearchMyTeamPartners } from 'hooks/useFetchGetShortMyTeamPartners';
import { useFetchBrDetails } from 'hooks/useFetchBrDetails';
import { PartnerToBrRoles } from 'types/BusinessRelationship';
import { ReactComponent as DropdownIcon } from 'assets/icons/IconDropdown.svg';
import { useFormikContext } from 'formik';
import ContactNote from 'types/ContactNote';
import { PartnerSearchResult } from 'views/ContactNoteForm/ContactNoteFormContainer';

interface Props {
  usedInBr: boolean;
}

const ContactNoteFormPartnerDropdown = ({ usedInBr }: Props) => {
  const { techId } = useParams<{ techId: string }>();
  const { values, setFieldValue, errors, touched } = useFormikContext<ContactNote>();
  const { t } = useTranslation();

  const { data: partnerSearchResults, isFetching: partnersLoading } = useSearchMyTeamPartners(usedInBr);

  const {
    data: brDetails,
    isFetching: brDetailsLoading,
    isSuccess: brLoadingSuccess,
  } = useFetchBrDetails(techId || '', usedInBr);

  const loading = partnersLoading || brDetailsLoading;

  const brRelatedPartners =
    brLoadingSuccess && !!brDetails
      ? brDetails.partnerToBrRoles.map((brRole: PartnerToBrRoles) => {
          return {
            label: brRole.partnerFullName,
            value: brRole.partnerUniqueId,
            ownPartner: false,
          };
        })
      : [];

  return (
    <>
      <InputLabel htmlFor="partner-autocomplete" required>
        {usedInBr ? t('noteForm.relatedPartners', 'Related partners') : t('noteForm.partner', 'Partner')}
      </InputLabel>
      <Autocomplete
        multiple={usedInBr}
        disableCloseOnSelect={usedInBr}
        options={usedInBr ? brRelatedPartners : (partnerSearchResults ?? [])}
        loading={loading}
        disabled={loading}
        popupIcon={
          loading ? (
            <CircularProgress
              size={12}
              sx={{ marginTop: '6px' }}
              id="partner-loading-indicator"
              data-testid="partner-loading-indicator"
            />
          ) : (
            <DropdownIcon />
          )
        }
        groupBy={
          usedInBr ? undefined : (option: PartnerSearchResult) => (option.ownPartner ? 'My partners' : 'Team partners')
        }
        getOptionLabel={(option) => option.label}
        id="partner-autocomplete"
        data-testid="partner-autocomplete"
        renderOption={(props, option, { selected }) => (
          <Box
            component="li"
            {...props}
            {...(usedInBr && { sx: { padding: '0 !important' } })}
            key={option.value?.toString()}
          >
            {usedInBr && <Checkbox style={{ marginRight: 8 }} checked={selected} />}
            {option.label}
          </Box>
        )}
        value={
          usedInBr
            ? brRelatedPartners.filter((brPartner) =>
                values.relatedPartners.some((relatedPartner) => relatedPartner.partnerUId === brPartner.value),
              )
            : partnerSearchResults?.find((partner) => partner['value'] === values.partner.partnerUId)
        }
        onChange={(e: React.ChangeEvent<unknown>, newValue: any) => {
          Array.isArray(newValue)
            ? setFieldValue(
                'relatedPartners',
                newValue.map((value: PartnerSearchResult) => {
                  return { partnerUId: value.value };
                }),
              )
            : setFieldValue('partner.partnerUId', newValue === null ? '' : newValue.value);
          setFieldValue('partner.fullName', newValue === null ? '' : (newValue as PartnerSearchResult).label);
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagPropsWithoutKey } = getTagProps({ index });
            return (
              <Chip
                key={key}
                variant="outlined"
                label={option.label}
                size="small"
                style={{
                  padding: '0 4px',
                  margin: '0 8px',
                  backgroundColor: 'white',
                }}
                {...tagPropsWithoutKey}
              />
            );
          })
        }
        renderInput={(params: AutocompleteRenderInputParams) => {
          return (
            <TextField
              {...params}
              helperText={
                usedInBr
                  ? touched.partner && values.relatedPartners?.length === 0 && (errors as any)?.relatedPartners
                  : touched.partner &&
                    !values.partner?.partnerUId &&
                    (errors as any)?.partner &&
                    (errors as any)?.partner?.partnerUId
              }
              error={
                usedInBr
                  ? touched.relatedPartners && !!(errors as any)?.relatedPartners
                  : touched.partner && !!(errors as any)?.partner?.partnerUId
              }
              fullWidth={true}
              variant="filled"
              InputProps={{
                ...params.InputProps,
              }}
            />
          );
        }}
      />
    </>
  );
};

export default ContactNoteFormPartnerDropdown;
